import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import {
  PrismButton,
  PrismCard,
  PrismLoadingSpinner,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import SettingsPmsIntegrationSeasonalStrategyCard from 'lumen/components/settings/pms-integration/seasonal-strategy-card';
import type { RecommendationStrategy } from 'lumen/models';
import type { RoomOffsetHierarchySettings } from 'lumen/models/recommendation-strategy';

interface SettingsPmsIntegrationSeasonalRoomHierarchyModalSignature {
  Args: {
    // The hotel default offsets, threaded to the editor for Reset-to-Default.
    defaultSettings?: RoomOffsetHierarchySettings | null;
    editingSeasonalStrategy: RecommendationStrategy | null;
    isLoading: boolean;
    isReadOnly: boolean;
    onClose: () => void;
    onCreateSeasonalStrategy: () => void;
    onDeleteSeasonalStrategy: (
      seasonalStrategy: RecommendationStrategy,
    ) => void;
    onEditSeasonalStrategy: (seasonalStrategy: RecommendationStrategy) => void;
    onEditorCancel: () => void;
    onSeasonalStrategySaved: (seasonalStrategy: RecommendationStrategy) => void;
    seasonalStrategies: RecommendationStrategy[];
  };
}

const SettingsPmsIntegrationSeasonalRoomHierarchyModal: TOC<SettingsPmsIntegrationSeasonalRoomHierarchyModalSignature> =
  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{t
        "pricing_assistant.recommendations.price_range.subtitle.seasonal_strategies"
      }}
    >
      <:default>
        <div class="modal--padded modal--medium">
          {{#if @isLoading}}
            <div
              class="flex items-center justify-center p-800"
              data-test-id="seasonal-strategies-loading"
            >
              <PrismLoadingSpinner />
            </div>
          {{else}}
            {{! The create action is right-aligned on its own row (empty state
              keeps its own centered CTA instead). }}
            <div class="mb-200 flex flex-row items-center justify-end">
              {{#if (or @editingSeasonalStrategy @seasonalStrategies.length)}}
                {{#unless @isReadOnly}}
                  <PrismButton
                    @buttonType="primary"
                    @isDisabled={{if @editingSeasonalStrategy true false}}
                    @label={{t
                      "pricing_assistant.recommendations.create_seasonal_strategy"
                    }}
                    @onClick={{@onCreateSeasonalStrategy}}
                    data-test-id="create-seasonal-strategy-button"
                  />
                {{/unless}}
              {{/if}}
            </div>

            {{#if @seasonalStrategies.length}}
              <div
                class="flex max-h-[560px] flex-col gap-200 overflow-y-auto"
                data-test-id="seasonal-strategy-list"
              >
                {{#each @seasonalStrategies as |seasonalStrategy|}}
                  <SettingsPmsIntegrationSeasonalStrategyCard
                    @defaultSettings={{@defaultSettings}}
                    @isEditing={{eq seasonalStrategy @editingSeasonalStrategy}}
                    @isReadOnly={{@isReadOnly}}
                    @onCancel={{@onEditorCancel}}
                    @onDelete={{fn @onDeleteSeasonalStrategy seasonalStrategy}}
                    @onEdit={{fn @onEditSeasonalStrategy seasonalStrategy}}
                    @onSaved={{@onSeasonalStrategySaved}}
                    @seasonalStrategies={{@seasonalStrategies}}
                    @strategy={{seasonalStrategy}}
                  />
                {{/each}}
              </div>
            {{else}}
              <PrismCard class="flex items-center justify-center pt-400">
                <:content>
                  <PrismMessage
                    @description={{t
                      "pricing_assistant.recommendations.price_range.empty_state.description"
                    }}
                    @illustration="100x100_chart"
                    @title={{t
                      "pricing_assistant.recommendations.empty_state.title"
                    }}
                    data-test-id="seasonal-strategies-empty-state"
                  >
                    <:primaryAction>
                      {{#unless @isReadOnly}}
                        <PrismButton
                          @buttonType="primary"
                          @label={{t
                            "pricing_assistant.recommendations.create_seasonal_strategy"
                          }}
                          @onClick={{@onCreateSeasonalStrategy}}
                          data-test-id="create-seasonal-strategy-button"
                        />
                      {{/unless}}
                    </:primaryAction>
                  </PrismMessage>
                </:content>
              </PrismCard>
            {{/if}}
          {{/if}}
        </div>
      </:default>
    </PrismModal>
  </template>;

export default SettingsPmsIntegrationSeasonalRoomHierarchyModal;
